"use strict";
//
// These data types make it easier to manage the state of data being fetched
// from the API.
//
Object.defineProperty(exports, "__esModule", { value: true });
exports.UnknownValue = void 0;
exports.isLoading = isLoading;
exports.getData = getData;
exports.hasData = hasData;
exports.isError = isError;
exports.isDone = isDone;
exports.toUnknown = toUnknown;
exports.toLoading = toLoading;
exports.toLoaded = toLoaded;
exports.initData = initData;
exports.nextData = nextData;
exports.splice = splice;
exports.toError = toError;
exports.apply = apply;
exports.filter = filter;
exports.map = map;
exports.reduce = reduce;
exports.sort = sort;
// Exported to allow consumers to use an unknown datum with a stable identity
// which is important for not triggering re-renders.
exports.UnknownValue = { kind: 'unknown' };
function isLoading(d) {
    return d.kind === 'loading' || d.kind === 'loading-data';
}
function getData(d) {
    if (d.kind === 'loading-data' || d.kind === 'loaded' || d.kind === 'error-data') {
        return d.data;
    }
    else {
        return null;
    }
}
function hasData(d) {
    return d.kind === 'loading-data' || d.kind === 'loaded' || d.kind === 'error-data';
}
function isError(d) {
    return d.kind === 'error' || d.kind === 'error-data';
}
/**
 * Whether the data is in an error or loaded state.
 */
function isDone(d) {
    return d.kind === 'error' || d.kind === 'error-data' || d.kind === 'loaded';
}
function toUnknown() {
    return exports.UnknownValue;
}
function toLoading(d) {
    if (isLoading(d)) {
        throw Error(`Unexpected state: ${d.kind}`);
    }
    if (hasData(d)) {
        return { kind: 'loading-data', data: d.data };
    }
    else {
        return { kind: 'loading' };
    }
}
function toLoaded(d) {
    if (!(isLoading(d) && hasData(d))) {
        throw Error(`Unexpected state: ${d.kind}`);
    }
    return { kind: 'loaded', data: d.data };
}
function initData(d, init) {
    if (!(isLoading(d) && !hasData(d))) {
        throw Error(`Unexpected state: ${d.kind}`);
    }
    return { kind: 'loading-data', data: init };
}
function nextData(d, next, f) {
    if (!(isLoading(d) && hasData(d))) {
        throw Error(`Unexpected state: ${d.kind}`);
    }
    return { kind: 'loading-data', data: f(d.data, next) };
}
function splice(d, start, deleteCount, ...items) {
    if (hasData(d)) {
        const newData = d.data.slice(0);
        newData.splice(start, deleteCount, ...items);
        return {
            kind: d.kind,
            data: newData,
        };
    }
    else {
        return d;
    }
}
function toError(d, error) {
    if (!isLoading(d)) {
        throw Error(`Unexpected state: ${d.kind}`);
    }
    if (hasData(d)) {
        return { kind: 'error-data', data: d.data, error };
    }
    else {
        return { kind: 'error', error };
    }
}
function apply(d, fn) {
    if (d.kind === 'error' || d.kind === 'loading' || d.kind === 'unknown') {
        return d;
    }
    else if (d.kind === 'loaded') {
        return {
            data: fn(d.data),
            kind: 'loaded',
        };
    }
    else if (d.kind === 'loading-data') {
        return {
            data: fn(d.data),
            kind: 'loading-data',
        };
    }
    else if (d.kind === 'error-data') {
        return {
            data: fn(d.data),
            kind: 'error-data',
        };
    }
    else {
        throw Error('Unexpected');
    }
}
function filter(d, f) {
    if (d.kind === 'error' || d.kind === 'loading' || d.kind === 'unknown') {
        return {
            kind: d.kind,
        };
    }
    else if (d.kind === 'loaded') {
        return {
            data: d.data.filter((e, index) => f(e, index)),
            kind: 'loaded',
        };
    }
    else if (d.kind === 'loading-data') {
        return {
            data: d.data.filter((e, index) => f(e, index)),
            kind: 'loading-data',
        };
    }
    else if (d.kind === 'error-data') {
        return {
            data: d.data.filter((e, index) => f(e, index)),
            kind: 'error-data',
        };
    }
    else {
        throw Error('Unexpected');
    }
}
function map(d, f) {
    if (d.kind === 'error' || d.kind === 'loading' || d.kind === 'unknown') {
        return {
            kind: d.kind,
        };
    }
    else if (d.kind === 'loaded') {
        return {
            data: d.data.map(e => f(e)),
            kind: 'loaded',
        };
    }
    else if (d.kind === 'loading-data') {
        return {
            data: d.data.map(e => f(e)),
            kind: 'loading-data',
        };
    }
    else if (d.kind === 'error-data') {
        return {
            data: d.data.map(e => f(e)),
            kind: 'error-data',
        };
    }
    else {
        throw Error('Unexpected');
    }
}
function reduce(d, init, f) {
    if (d.kind === 'error' || d.kind === 'loading' || d.kind === 'unknown') {
        return {
            kind: d.kind,
        };
    }
    else if (d.kind === 'loaded') {
        return {
            data: d.data.reduce(f, init),
            kind: 'loaded',
        };
    }
    else if (d.kind === 'loading-data') {
        return {
            data: d.data.reduce(f, init),
            kind: 'loading-data',
        };
    }
    else if (d.kind === 'error-data') {
        return {
            data: d.data.reduce(f, init),
            kind: 'error-data',
        };
    }
    else {
        throw Error('Unexpected');
    }
}
function sort(d, cmp) {
    if (d.kind === 'error' || d.kind === 'loading' || d.kind === 'unknown') {
        return {
            kind: d.kind,
        };
    }
    else if (d.kind === 'loaded') {
        return {
            data: [...d.data].sort(cmp),
            kind: 'loaded',
        };
    }
    else if (d.kind === 'loading-data') {
        return {
            data: [...d.data].sort(cmp),
            kind: 'loading-data',
        };
    }
    else if (d.kind === 'error-data') {
        return {
            data: [...d.data].sort(cmp),
            kind: 'error-data',
        };
    }
    else {
        throw Error('Unexpected');
    }
}
